import { Badge, classesBouton } from '@artosera/ui';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ArtistePresente } from '../../api/artistes.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { dateContrat } from '../contrats/contratUi.js';
import styles from './ArtistesTableau.module.css';

type Colonne = 'nom' | 'nationalite' | 'oeuvres' | 'disponibles' | 'representation' | 'echeance';

interface Props {
  readonly artistes: readonly ArtistePresente[];
  readonly galerieSlug: string;
  readonly portraits: Readonly<Record<string, string>>;
  /** Nombre d'œuvres disponibles par artiste (identifiant → nombre). */
  readonly disponibles: ReadonlyMap<string, number>;
}

/** Vue liste des artistes : tableau dense, triable par colonne. */
export function ArtistesTableau({
  artistes,
  galerieSlug,
  portraits,
  disponibles,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation(['regie']);
  const [tri, setTri] = useState<{ colonne: Colonne; sens: 1 | -1 }>({ colonne: 'nom', sens: 1 });

  const valeur = (a: ArtistePresente, colonne: Colonne): string | number => {
    switch (colonne) {
      case 'nom':
        return a.nom;
      case 'nationalite':
        return a.nationalite ?? '';
      case 'oeuvres':
        return a.nombreOeuvres ?? 0;
      case 'disponibles':
        return disponibles.get(a.id) ?? 0;
      case 'representation':
        return a.representation.representee ? 1 : 0;
      case 'echeance':
        // Sans échéance en dernier, quel que soit le sens.
        return a.representation.jusquau ?? '';
    }
  };

  const tries = [...artistes].sort((a, b) => {
    const va = valeur(a, tri.colonne);
    const vb = valeur(b, tri.colonne);
    if (tri.colonne === 'echeance') {
      if (va === '' && vb !== '') return 1;
      if (vb === '' && va !== '') return -1;
    }
    const ordre =
      typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), i18n.language);
    return ordre * tri.sens;
  });

  function entete(colonne: Colonne, libelle: string, secondaire = false): JSX.Element {
    const actif = tri.colonne === colonne;
    return (
      <th
        scope="col"
        className={secondaire ? styles.secondaire : undefined}
        aria-sort={actif ? (tri.sens === 1 ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          className={styles.tri}
          onClick={() => setTri({ colonne, sens: actif && tri.sens === 1 ? -1 : 1 })}
        >
          {libelle}
          {actif ? (
            tri.sens === 1 ? (
              <ArrowUp size={12} strokeWidth={1.5} aria-hidden />
            ) : (
              <ArrowDown size={12} strokeWidth={1.5} aria-hidden />
            )
          ) : null}
        </button>
      </th>
    );
  }

  return (
    <div className={styles.cadre}>
      <table className={styles.tableau}>
        <thead>
          <tr>
            {entete('nom', t('regie:artistes.colonneNom'))}
            {entete('nationalite', t('regie:artistes.liste.colonneNationalite'), true)}
            {entete('oeuvres', t('regie:artistes.colonneOeuvres'))}
            {entete('disponibles', t('regie:artistes.liste.colonneDisponibles'), true)}
            {entete('representation', t('regie:artistes.colonneRepresentation'))}
            {entete('echeance', t('regie:artistes.liste.colonneEcheance'), true)}
            <th scope="col" className={styles.secondaire}>
              <span className={styles.sr}>{t('regie:artistes.liste.colonneActions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {tries.map((a) => (
            <tr key={a.id}>
              <td>
                <Link className={styles.identite} to={`/${galerieSlug}/artistes/${a.id}`}>
                  <Monogramme
                    nom={a.nom}
                    portraitUrl={portraits[a.id] ?? null}
                    taille={32}
                    forme="carre"
                  />
                  <span className={styles.nom}>{a.nom}</span>
                  {a.naissance ? <span className={styles.naissance}>{a.naissance}</span> : null}
                </Link>
              </td>
              <td className={styles.secondaire}>{a.nationalite ?? '—'}</td>
              <td className={styles.nombre}>{a.nombreOeuvres ?? 0}</td>
              <td className={`${styles.nombre} ${styles.secondaire}`}>
                {disponibles.get(a.id) ?? 0}
              </td>
              <td>
                <span className={styles.badges}>
                  <Badge tone={a.representation.representee ? 'accent' : 'neutral'}>
                    {a.representation.representee
                      ? t('regie:artistes.representee')
                      : t('regie:artistes.nonRepresentee')}
                  </Badge>
                  {a.archive ? <Badge tone="withdrawn">{t('regie:artistes.archive')}</Badge> : null}
                </span>
              </td>
              <td className={styles.secondaire}>
                {a.representation.jusquau
                  ? dateContrat(a.representation.jusquau, i18n.language)
                  : '—'}
              </td>
              <td className={styles.secondaire}>
                <Link
                  className={classesBouton('secondary', 'sm')}
                  to={`/${galerieSlug}/artistes/${a.id}`}
                >
                  {t('regie:artistes.liste.ouvrir')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
